'use client';

import { signIn } from 'next-auth/react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { FormField } from '~/components/core/FormField';
import Button from '~/components/customComponents/buttons/Button';
import ErrorBanner from '~/components/customComponents/error-banner/ErrorBanner';
import Flex from '~/components/customComponents/flex/Flex';
import Text from '~/components/customComponents/texts/Text';
import { ROUTES_MAP } from '~/constants/routes';
import { usePostHog } from '~/hooks/usePostHog';
import { useCurrentLocale, useI18n } from '~/locales/client';
import { AuthErrorTypeEnum, getAuthErrorType } from '~/utils/authUtils';
import DeactivatedModal from '../_components/DeactivatedModal/DeactivatedModal';

/**
 * Renders login form component to handle user login
 */
const LoginForm = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const translate = useI18n();
  const locale = useCurrentLocale();
  const { captureEvent } = usePostHog();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Translations (memoized to prevent re-renders)
  const copy = useMemo(
    () => ({
      titleLabel: translate('page.login.view.title'),
      invalidCredentialsLabel: translate('page.login.view.invalid-credentials.label'),
      deactivatedLabel: translate('page.login.view.deactivated.label'),
      somethingWrongLabel: translate('page.login.view.something.wrong.label'),
      emailLabel: translate('page.login.view.email.label'),
      passwordLabel: translate('page.login.view.password.label'),
      forgotPasswordLabel: translate('page.login.view.forgot-password.label'),
      forgotPasswordHref: translate('page.login.view.forgot-password.href'),
      buttonLabel: translate('page.login.view.button.label'),
    }),
    [translate],
  );

  // Reset error when email or password changes
  useEffect(() => {
    setIsError(false);
    setErrorMessage('');
  }, [email, password]);

  // Handle authentication error types
  const handleError = (errorType: AuthErrorTypeEnum) => {
    setErrorMessage(
      errorType === AuthErrorTypeEnum.DEACTIVATED
        ? copy.deactivatedLabel
        : errorType === AuthErrorTypeEnum.INVALID_CREDENTIALS
        ? copy.invalidCredentialsLabel
        : copy.somethingWrongLabel,
    );
    setIsError(true);

    if (errorType === AuthErrorTypeEnum.DEACTIVATED) {
      setIsModalOpen(true);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    const trimmedEmail = email.trim();

    try {
      const result = await signIn('credentials', {
        redirect: false,
        email: trimmedEmail,
        password,
      });

      if (result?.ok) {
        captureEvent('user_logged_in', { email: trimmedEmail });
        const callbackUrl = searchParams.get('callbackUrl');
        router.replace(callbackUrl || `/${locale}${ROUTES_MAP.home}`);
      } else {
        const errorType = getAuthErrorType(result?.error ?? '');
        captureEvent('user_login_failed', { email: trimmedEmail, error: result?.error ?? '' });
        handleError(errorType);
        setLoading(false);
      }
    } catch (error) {
      captureEvent('user_login_failed', { email: trimmedEmail, error: String(error) });
      handleError(getAuthErrorType(error as unknown as string));
      setLoading(false);
    }
  };

  const isCTADisabled = Boolean(!email?.length || !password?.length);

  return (
    <Flex className="items-center justify-center min-w-full">
      <Flex className="flex-col justify-center w-full max-w-96">
        <Text variant="h1" className="mb-6">
          {copy.titleLabel}
        </Text>
        <form onSubmit={handleSubmit}>
          <FormField
            label={copy.emailLabel}
            name="email"
            type="email"
            onChange={(value) => setEmail(value.toLowerCase())}
            isInvalid={isError}
            value={email}
          />
          <FormField
            label={copy.passwordLabel}
            name="password"
            type="password"
            onChange={setPassword}
            value={password}
            isInvalid={isError}
          />

          {isError && (
            <ErrorBanner containerClassName="mb-2.5" withIcon>
              {errorMessage}
            </ErrorBanner>
          )}

          <Button
            disabled={isCTADisabled || loading}
            type="submit"
            text={copy.buttonLabel}
            buttonClassName={isCTADisabled ? '' : 'hover:bg-red-600'}
            isLoading={loading}
          />
        </form>

        <Flex className="justify-center mt-2">
          <Link href={copy.forgotPasswordHref} className="text-base hover:underline">
            {copy.forgotPasswordLabel}
          </Link>
        </Flex>
        <Link href={ROUTES_MAP.home} prefetch={true} className="hidden" />
      </Flex>

      {/* Deactivated Modal */}
      <DeactivatedModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Flex>
  );
};

export default LoginForm;
